/**
 * Traducción a criollo del People-flow: el panel lo usa gente de People, no
 * gente técnica. Acá viven los nombres humanos de cada paso y la explicación
 * de cada error — qué pasó y qué hacer — para que nadie tenga que interpretar
 * `day1_dm` ni "users_not_found".
 *
 * Regla: nada de códigos técnicos a la vista. Si el mensaje crudo del sistema
 * no matchea ningún patrón conocido, se muestra tal cual (mejor eso que
 * esconder información), pero siempre precedido de qué se estaba intentando.
 *
 * Todo recibe `t`: los textos viven en los locales (users.plain.*) y este
 * archivo no es un componente, así que no puede usar el hook. Nada acá se
 * evalúa a nivel de módulo — un const con t() afuera de un componente es `t`
 * indefinida en runtime.
 */
import type { useTranslation } from 'react-i18next';

type TFunction = ReturnType<typeof useTranslation>['t'];

/** Qué hace cada paso, contado como se lo contarías a alguien de People. */
export const STEP_PLAIN = (t: TFunction): Record<string, { title: string; what: string }> => ({
  validation: { title: t('users.plain.step.validation.title'), what: t('users.plain.step.validation.what') },
  cal_day1: { title: t('users.plain.step.cal_day1.title'), what: t('users.plain.step.cal_day1.what') },
  cal_leader: { title: t('users.plain.step.cal_leader.title'), what: t('users.plain.step.cal_leader.what') },
  rep_reminder: { title: t('users.plain.step.rep_reminder.title'), what: t('users.plain.step.rep_reminder.what') },
  leader_notice: { title: t('users.plain.step.leader_notice.title'), what: t('users.plain.step.leader_notice.what') },
  day1_dm: { title: t('users.plain.step.day1_dm.title'), what: t('users.plain.step.day1_dm.what') },
  channels_dm: { title: t('users.plain.step.channels_dm.title'), what: t('users.plain.step.channels_dm.what') },
  learning_dm: { title: t('users.plain.step.learning_dm.title'), what: t('users.plain.step.learning_dm.what') },
  whatsapp_dm: { title: t('users.plain.step.whatsapp_dm.title'), what: t('users.plain.step.whatsapp_dm.what') },
  week1_form_dm: { title: t('users.plain.step.week1_form_dm.title'), what: t('users.plain.step.week1_form_dm.what') },
  global_dm: { title: t('users.plain.step.global_dm.title'), what: t('users.plain.step.global_dm.what') },
  global_followup_dm: { title: t('users.plain.step.global_followup_dm.title'), what: t('users.plain.step.global_followup_dm.what') },
  checkin30_dm: { title: t('users.plain.step.checkin30_dm.title'), what: t('users.plain.step.checkin30_dm.what') },
  alta_account: { title: t('users.plain.step.alta_account.title'), what: t('users.plain.step.alta_account.what') },
});

export function stepTitle(t: TFunction, stepId: string | undefined): string {
  if (!stepId) return t('users.plain.step_fallback');
  return STEP_PLAIN(t)[stepId]?.title ?? stepId;
}

/** Estados, contados en criollo. */
export const STATUS_PLAIN = (t: TFunction): Record<string, string> => ({
  ok: t('users.plain.status.ok'),
  scheduled: t('users.plain.status.scheduled'),
  waiting: t('users.plain.status.waiting'),
  error: t('users.plain.status.error'),
  expired: t('users.plain.status.expired'),
  blocked_validation: t('users.plain.status.blocked_validation'),
});

/**
 * Traduce el mensaje crudo del sistema a "qué pasó" + "qué hacer".
 * `raw` es lo que guardó el motor (ej. `error: DM a x@ falló: users_not_found`).
 */
export function explainError(
  t: TFunction,
  raw: string | null | undefined,
  stepId?: string
): { what: string; todo: string; technical?: string } {
  const msg = (raw ?? '').replace(/^error:\s*/i, '').trim();
  const lower = msg.toLowerCase();
  const step = stepTitle(t, stepId);

  if (!msg) {
    return { what: t('users.plain.error.generic.what', { step }), todo: t('users.plain.error.generic.todo_empty') };
  }

  // Google Workspace sin licencias (alta de la cuenta): decir qué pasó y dónde
  // se compra, con el link (Sol 15/09).
  if (lower.includes('insufficient licenses') || lower.includes('licencias libres')) {
    return { what: t('users.plain.error.no_licenses.what'), todo: t('users.plain.error.no_licenses.todo'), technical: msg };
  }
  if (lower.includes('users_not_found') || lower.includes('user_not_found')) {
    return { what: t('users.plain.error.slack_user.what'), todo: t('users.plain.error.slack_user.todo'), technical: msg };
  }
  if (lower.includes('channel_not_found')) {
    return { what: t('users.plain.error.slack_channel.what'), todo: t('users.plain.error.slack_channel.todo'), technical: msg };
  }
  if (lower.includes('not_in_channel') || lower.includes('is_archived')) {
    return { what: t('users.plain.error.slack_archived.what'), todo: t('users.plain.error.slack_archived.todo'), technical: msg };
  }
  if (lower.includes('calendar api has not been used') || lower.includes('accessnotconfigured')) {
    return { what: t('users.plain.error.calendar_api.what'), todo: t('users.plain.error.calendar_api.todo'), technical: msg };
  }
  if (lower.includes('correo válido para invitar')) {
    return { what: t('users.plain.error.no_email.what'), todo: t('users.plain.error.no_email.todo'), technical: msg };
  }
  if (lower.includes('mail people manager')) {
    return { what: t('users.plain.error.no_leader.what'), todo: t('users.plain.error.no_leader.todo'), technical: msg };
  }
  if (lower.includes('forbidden') || lower.includes('permission') || lower.includes('403')) {
    return { what: t('users.plain.error.forbidden.what'), todo: t('users.plain.error.forbidden.todo'), technical: msg };
  }
  if (lower.includes('not found') || lower.includes('404')) {
    return { what: t('users.plain.error.not_found.what', { step }), todo: t('users.plain.error.not_found.todo'), technical: msg };
  }
  if (lower.includes('quota') || lower.includes('rate') || lower.includes('timeout') || lower.includes('econn')) {
    return { what: t('users.plain.error.timeout.what'), todo: t('users.plain.error.timeout.todo'), technical: msg };
  }
  if (lower.includes('template') && lower.includes('no encontrado')) {
    return { what: t('users.plain.error.template.what'), todo: t('users.plain.error.template.todo'), technical: msg };
  }
  if (lower.includes('destinatario inválido')) {
    // "destinatario inválido" siempre significa que falta UN dato concreto, y
    // cuál depende del paso. Decir "el destinatario no es válido" obligaba a
    // People a adivinar (feedback Sol 10/09): acá se nombra el dato.
    if (stepId === 'rep_reminder') {
      return { what: t('users.plain.error.recipient.what_rep'), todo: t('users.plain.error.recipient.todo_rep'), technical: msg };
    }
    const field = RECIPIENT_FIELD_BY_STEP[stepId ?? ''];
    if (field) {
      const label = FIELD_LABEL_PLAIN(t)[field] ?? field;
      return {
        what: t('users.plain.error.recipient.what_field', { field: label }),
        todo: t('users.plain.error.recipient.todo_field', { field: label }),
        technical: msg,
      };
    }
    return { what: t('users.plain.error.recipient.what'), todo: t('users.plain.error.recipient.todo'), technical: msg };
  }

  return { what: t('users.plain.error.generic.what', { step }), todo: t('users.plain.error.generic.todo_detail'), technical: msg };
}

/** Estados "buenos" que el motor guarda con matices (para contarlos bien). */
export const RESULT_PLAIN = (t: TFunction): Record<string, string> => ({
  enviado: t('users.plain.result.enviado'),
  creado: t('users.plain.result.creado'),
  creado_sin_sala: t('users.plain.result.creado_sin_sala'),
  creado_sin_hueco: t('users.plain.result.creado_sin_hueco'),
  mail_fallback: t('users.plain.result.mail_fallback'),
  completa: t('users.plain.result.completa'),
  incompleta: t('users.plain.result.incompleta'),
  expirado: t('users.plain.result.expirado'),
  no_aplica: t('users.plain.result.no_aplica'),
  na: t('users.plain.result.na'),
});

/** Acciones del historial, en criollo (comparte semántica con el audit). */
export const ACTION_PLAIN = (t: TFunction): Record<string, string> => ({
  sent: t('users.plain.action.sent'),
  created: t('users.plain.action.created'),
  error: t('users.plain.action.error'),
  retried: t('users.plain.action.retried'),
  forced: t('users.plain.action.forced'),
  marked_done: t('users.plain.action.marked_done'),
  paused: t('users.plain.action.paused'),
  resumed: t('users.plain.action.resumed'),
  expired: t('users.plain.action.expired'),
  waiting: t('users.plain.action.waiting'),
  skipped_validation: t('users.plain.action.skipped_validation'),
  flags_reset: t('users.plain.action.flags_reset'),
  validation_notified: t('users.plain.action.validation_notified'),
  config_changed: t('users.plain.action.config_changed'),
  template_changed: t('users.plain.action.template_changed'),
});

// ─── Qué dato falta (tab Enviados) ───────────────────────────────────────────

/**
 * Columnas de la planilla "Altas 2025", en criollo. Espejo de FIELD_LABELS del
 * backend (people-flow-steps.ts): el audit guarda los ids crudos
 * ('telefono,foto') y el panel tiene que mostrar los nombres de la planilla.
 */
export const FIELD_LABEL_PLAIN = (t: TFunction): Record<string, string> => ({
  nombre: t('users.plain.field.nombre'),
  correoPersonal: t('users.plain.field.correoPersonal'),
  correoWinclap: t('users.plain.field.correoWinclap'),
  pais: t('users.plain.field.pais'),
  comunidad: t('users.plain.field.comunidad'),
  rol: t('users.plain.field.rol'),
  impactLevel: t('users.plain.field.impactLevel'),
  team: t('users.plain.field.team'),
  area: t('users.plain.field.area'),
  managerEmail: t('users.plain.field.managerEmail'),
  recruiterEmail: t('users.plain.field.recruiterEmail'),
  identificacion: t('users.plain.field.identificacion'),
  direccion: t('users.plain.field.direccion'),
  telefono: t('users.plain.field.telefono'),
  foto: t('users.plain.field.foto'),
  idFrente: t('users.plain.field.idFrente'),
  idDorso: t('users.plain.field.idDorso'),
  hobby: t('users.plain.field.hobby'),
  funFact: t('users.plain.field.funFact'),
});

/** Dato de la planilla que cada paso usa como destinatario. */
const RECIPIENT_FIELD_BY_STEP: Record<string, string> = {
  leader_notice: 'managerEmail',
  day1_dm: 'correoWinclap',
  channels_dm: 'correoWinclap',
  learning_dm: 'correoWinclap',
  whatsapp_dm: 'correoWinclap',
  week1_form_dm: 'correoWinclap',
  checkin30_dm: 'correoWinclap',
  global_dm: 'correoWinclap',
  global_followup_dm: 'correoWinclap',
};

/** 'telefono,foto' → 'Número de contacto, Foto personal'. */
export function fieldsPlain(t: TFunction, csv: string): string {
  const labels = FIELD_LABEL_PLAIN(t);
  return csv
    .split(/[,;]/)
    .map((f) => f.trim())
    .filter(Boolean)
    .map((f) => labels[f] ?? f)
    .join(', ');
}

/**
 * El renglón del historial (tab Enviados) contado en criollo: qué pasó y, si
 * el problema es un dato que falta, CUÁL. El detalle crudo del motor solo se
 * muestra cuando no matchea ningún patrón conocido.
 */
export function actionDetailPlain(
  t: TFunction,
  entry: { action: string; stepId?: string; detail?: string }
): { text: string; todo?: string } | null {
  const detail = (entry.detail ?? '').trim();

  // Los datos faltantes van PRIMERO: el caso más común de "no se pudo" es una
  // columna vacía de la planilla, y ahí lo único que importa es cuál.
  // 'faltan: telefono,foto' (aviso al recruiter)
  const faltan = detail.match(/faltan:\s*([^—]+)/i);
  if (faltan) {
    return { text: t('users.plain.detail.missing', { fields: fieldsPlain(t, faltan[1]) }) };
  }

  // 'Paso vencido pero bloqueado por datos faltantes (telefono,foto) — …'
  const blocked = detail.match(/datos faltantes \(([^)]*)\)/i);
  if (blocked) {
    const fields = fieldsPlain(t, blocked[1]);
    return {
      text: fields
        ? t('users.plain.detail.blocked', { fields })
        : t('users.plain.detail.blocked_unknown'),
      todo: t('users.plain.detail.blocked_todo'),
    };
  }

  if (entry.action === 'error') {
    const e = explainError(t, detail, entry.stepId);
    return { text: e.what, todo: e.todo };
  }

  if (entry.action === 'expired' && !detail) {
    return { text: t('users.plain.detail.expired_window') };
  }

  // '→ nombre@winclap.com' / '→ nombre@winclap.com (Slack + mail de People)'
  const to = detail.match(/^→\s*(\S+@\S+?)\s*(?:\((.+)\))?$/);
  if (to) {
    return {
      text: to[2]
        ? t('users.plain.detail.sent_to_via', { to: to[1], via: to[2] })
        : t('users.plain.detail.sent_to', { to: to[1] }),
    };
  }

  return detail ? { text: detail } : null;
}
